'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';

interface BookingStep {
  label: string;
  description: string;
  icon: string;
}

interface BookingProgressIndicatorProps {
  currentStep: number;
  onStepClick?: (step: number) => void;
  allowBackNavigation?: boolean;
  steps?: string[];
}

const BookingProgressIndicator = ({
  currentStep = 1,
  onStepClick,
  allowBackNavigation = true,
  steps: customSteps,
}: BookingProgressIndicatorProps) => {
  const defaultSteps: BookingStep[] = [
    {
      label: 'Service Selection',
      description: 'Choose your service',
      icon: 'ClipboardDocumentCheckIcon',
    },
    {
      label: 'Schedule',
      description: 'Pick date and time',
      icon: 'CalendarIcon',
    },
    {
      label: 'Details',
      description: 'Provide information',
      icon: 'DocumentTextIcon',
    },
    {
      label: 'Confirmation',
      description: 'Review and confirm',
      icon: 'CheckCircleIcon',
    },
  ];

  // If custom steps provided, map them to BookingStep format
  const steps: BookingStep[] = customSteps
    ? customSteps.map((label, index) => ({
        label,
        description: getStepDescription(label),
        icon: getStepIcon(label),
      }))
    : defaultSteps;

  function getStepDescription(label: string): string {
    const descriptions: Record<string, string> = {
      'Service Selection': 'Choose service',
      Service: 'Choose your service',
      Package: 'Select package',
      'Add-ons': 'Optional extras',
      Measurements: 'Provide details',
      Verification: 'AI photo check',
      Schedule: 'Pick date & time',
      Details: 'Your information',
      'Address Details': 'Where to apply',
      Summary: 'Review booking',
      Payment: 'Complete payment',
    };
    return descriptions[label] || label;
  }

  function getStepIcon(label: string): string {
    const icons: Record<string, string> = {
      'Service Selection': 'ClipboardDocumentCheckIcon',
      Service: 'ClipboardDocumentCheckIcon',
      Package: 'CubeIcon',
      'Add-ons': 'SparklesIcon',
      Measurements: 'RulerIcon',
      Schedule: 'CalendarIcon',
      Details: 'DocumentTextIcon',
      'Address Details': 'MapPinIcon',
      Summary: 'DocumentCheckIcon',
      Payment: 'CreditCardIcon',
    };
    return icons[label] || 'CheckCircleIcon';
  }

  const handleStepClick = (stepIndex: number) => {
    if (allowBackNavigation && stepIndex < currentStep && onStepClick) {
      onStepClick(stepIndex + 1);
    }
  };

  const getStepStatus = (stepIndex: number) => {
    if (stepIndex + 1 < currentStep) return 'completed';
    if (stepIndex + 1 === currentStep) return 'active';
    return 'pending';
  };

  return (
    <div data-component-id="src/components/common/BookingProgressIndicator.tsx:104:4" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="104" data-component-end-line="232" data-component-file="BookingProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20bg-white%20border-b%20border-gray-100%20shadow-%5B0_2px_20px_rgb(0%2C0%2C0%2C0.04)%5D%22%7D" className="w-full bg-white border-b border-gray-100 shadow-[0_2px_20px_rgb(0,0,0,0.04)]">
      <div data-component-id="src/components/common/BookingProgressIndicator.tsx:105:6" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="105" data-component-end-line="231" data-component-file="BookingProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-6xl%20mx-auto%20px-4%20md%3Apx-8%20py-4%20md%3Apy-5%22%7D" className="max-w-6xl mx-auto px-4 md:px-8 py-4 md:py-5">
        {/* Desktop Progress */}
        <div data-component-id="src/components/common/BookingProgressIndicator.tsx:107:8" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="107" data-component-end-line="174" data-component-file="BookingProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20md%3Aflex%20items-start%22%7D" className="hidden md:flex items-start">
          {steps.map((step, index) => {
            const status = getStepStatus(index);
            const isClickable = allowBackNavigation && index + 1 < currentStep;
            const isLast = index === steps.length - 1;

            return (
              <React.Fragment key={index}>
                <motion.button data-component-id="src/components/common/BookingProgressIndicator.tsx:115:16" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="115" data-component-end-line="158" data-component-file="BookingProgressIndicator.tsx" data-component-name="motion.button" data-component-content="%7B%22elementName%22%3A%22motion.button%22%7D"
                  onClick={() => handleStepClick(index)}
                  disabled={!isClickable}
                  whileHover={isClickable ? { scale: 1.04 } : {}}
                  whileTap={isClickable ? { scale: 0.96 } : {}}
                  className={`flex flex-col items-center gap-2 w-[110px] flex-shrink-0 transition-all duration-300 ${
                    isClickable ? 'cursor-pointer' : 'cursor-default'
                  }`}
                  data-testid={`step-${index + 1}`}
                >
                  <motion.div data-component-id="src/components/common/BookingProgressIndicator.tsx:125:18" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="125" data-component-end-line="141" data-component-file="BookingProgressIndicator.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%7D"
                    className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 ${
                      status === 'completed'
                        ? 'bg-[#204653] text-white shadow-[0_4px_15px_rgb(74,124,126,0.3)]'
                        : status === 'active'
                          ? 'bg-[#1D1D1F] text-white shadow-[0_4px_15px_rgb(29,29,31,0.2)]'
                          : 'bg-[#F5F5F7] text-[#86868B]'
                    }`}
                    animate={status === 'active' ? { scale: [1, 1.05, 1] } : {}}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    {status === 'completed' ? (
                      <Icon data-component-id="src/components/common/BookingProgressIndicator.tsx:137:22" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="137" data-component-end-line="137" data-component-file="BookingProgressIndicator.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckIcon%22%7D" name="CheckIcon" size={20} />
                    ) : (
                      <Icon data-component-id="src/components/common/BookingProgressIndicator.tsx:139:22" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="139" data-component-end-line="139" data-component-file="BookingProgressIndicator.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={step.icon as any} size={20} />
                    )}
                  </motion.div>
                  <div data-component-id="src/components/common/BookingProgressIndicator.tsx:142:18" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="142" data-component-end-line="157" data-component-file="BookingProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20min-h-%5B34px%5D%22%7D" className="text-center min-h-[34px]">
                    <div data-component-id="src/components/common/BookingProgressIndicator.tsx:143:20" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="143" data-component-end-line="153" data-component-file="BookingProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                      className={`text-[13px] leading-tight font-semibold font-heading ${
                        status === 'active'
                          ? 'text-[#1D1D1F]'
                          : status === 'completed'
                            ? 'text-[#204653]'
                            : 'text-[#86868B]'
                      }`}
                    >
                      {step.label}
                    </div>
                    <div data-component-id="src/components/common/BookingProgressIndicator.tsx:154:20" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="154" data-component-end-line="156" data-component-file="BookingProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-%5B11px%5D%20leading-tight%20text-%5B%2386868B%5D%20mt-0.5%22%7D" className="text-[11px] leading-tight text-[#86868B] mt-0.5">
                      {step.description}
                    </div>
                  </div>
                </motion.button>

                {!isLast && (
                  <div data-component-id="src/components/common/BookingProgressIndicator.tsx:161:18" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="161" data-component-end-line="169" data-component-file="BookingProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20h-0.5%20mt-%5B22px%5D%20mx-2%20relative%20overflow-hidden%20rounded-full%20min-w-%5B16px%5D%22%7D" className="flex-1 h-0.5 mt-[22px] mx-2 relative overflow-hidden rounded-full min-w-[16px]">
                    <div data-component-id="src/components/common/BookingProgressIndicator.tsx:162:20" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="162" data-component-end-line="162" data-component-file="BookingProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-%5B%23F5F5F7%5D%22%7D" className="absolute inset-0 bg-[#F5F5F7]" />
                    <motion.div data-component-id="src/components/common/BookingProgressIndicator.tsx:163:20" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="163" data-component-end-line="168" data-component-file="BookingProgressIndicator.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-%5B%23204653%5D%22%7D"
                      className="absolute inset-0 bg-[#204653]"
                      initial={{ width: 0 }}
                      animate={{ width: index + 1 < currentStep ? '100%' : '0%' }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Mobile Progress */}
        <div data-component-id="src/components/common/BookingProgressIndicator.tsx:177:8" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="177" data-component-end-line="230" data-component-file="BookingProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22md%3Ahidden%20space-y-3%22%7D" className="md:hidden space-y-3">
          {steps.map((step, index) => {
            const status = getStepStatus(index);
            const isClickable = allowBackNavigation && index + 1 < currentStep;

            return (
              <motion.button data-component-id="src/components/common/BookingProgressIndicator.tsx:183:14" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="183" data-component-end-line="227" data-component-file="BookingProgressIndicator.tsx" data-component-name="motion.button" data-component-content="%7B%22elementName%22%3A%22motion.button%22%7D"
                key={index}
                onClick={() => handleStepClick(index)}
                disabled={!isClickable}
                whileTap={isClickable ? { scale: 0.98 } : {}}
                className={`flex items-center gap-4 w-full p-4 rounded-2xl transition-all duration-300 ${
                  status === 'active' 
                    ? 'bg-[#1D1D1F] text-white shadow-[0_4px_20px_rgb(29,29,31,0.15)]'
                    : status === 'completed' 
                      ? 'bg-[#E4EDF0] border border-[#204653]/20' 
                      : 'bg-[#F5F5F7]'
                } ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
                data-testid={`mobile-step-${index + 1}`}
              >
                <div data-component-id="src/components/common/BookingProgressIndicator.tsx:197:16" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="197" data-component-end-line="211" data-component-file="BookingProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                  className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    status === 'completed'
                      ? 'bg-[#204653] text-white'
                      : status === 'active' 
                        ? 'bg-white text-[#1D1D1F]' 
                        : 'bg-white text-[#86868B]'
                  }`}
                >
                  {status === 'completed' ? (
                    <Icon data-component-id="src/components/common/BookingProgressIndicator.tsx:207:20" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="207" data-component-end-line="207" data-component-file="BookingProgressIndicator.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckIcon%22%7D" name="CheckIcon" size={20} />
                  ) : (
                    <span data-component-id="src/components/common/BookingProgressIndicator.tsx:209:20" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="209" data-component-end-line="209" data-component-file="BookingProgressIndicator.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-bold%20font-heading%22%7D" className="font-bold font-heading">{index + 1}</span>
                  )}
                </div>
                <div data-component-id="src/components/common/BookingProgressIndicator.tsx:212:16" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="212" data-component-end-line="223" data-component-file="BookingProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20text-left%22%7D" className="flex-1 text-left">
                  <div data-component-id="src/components/common/BookingProgressIndicator.tsx:213:18" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="213" data-component-end-line="217" data-component-file="BookingProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`font-semibold font-heading ${
                    status === 'active' ? 'text-white' : status === 'completed' ? 'text-[#204653]' : 'text-[#1D1D1F]'
                  }`}>
                    {step.label}
                  </div>
                  <div data-component-id="src/components/common/BookingProgressIndicator.tsx:218:18" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="218" data-component-end-line="222" data-component-file="BookingProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`text-sm ${
                    status === 'active' ? 'text-gray-300' : 'text-[#86868B]'
                  }`}>
                    {step.description}
                  </div>
                </div>
                {status === 'active' && (
                  <Icon data-component-id="src/components/common/BookingProgressIndicator.tsx:225:18" data-component-path="src/components/common/BookingProgressIndicator.tsx" data-component-line="225" data-component-end-line="225" data-component-file="BookingProgressIndicator.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronRightIcon%22%2C%22className%22%3A%22text-white%22%7D" name="ChevronRightIcon" size={20} className="text-white" />
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BookingProgressIndicator;
